/**
 * Header-agnostic predictive search suggestions.
 * Wraps Shopify's Predictive Search API (window.routes.predictive_search_url).
 * Usage: new PredictiveSearchSuggestions(inputEl, containerEl, options)
 */
(function () {
  function debounce(fn, wait) {
    var timeoutId;
    return function debounced() {
      var args = arguments;
      var context = this;
      clearTimeout(timeoutId);
      timeoutId = setTimeout(function () {
        fn.apply(context, args);
      }, wait);
    };
  }

  function escapeHtml(value) {
    var div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  }

  function normalizeQuery(query) {
    return query.trim().replace(/\s+/g, ' ').toLowerCase();
  }

  var STATES = {
    IDLE: 'idle',
    LOADING: 'loading',
    RESULTS: 'results',
    NO_RESULTS: 'no-results',
    ERROR: 'error',
  };

  function PredictiveSearchSuggestions(input, container, options) {
    if (!input || !container) {
      throw new Error('PredictiveSearchSuggestions requires an input element and a container element');
    }

    this.input = input;
    this.container = container;
    this.options = Object.assign(
      {
        resultLimit: 4,
        debounceMs: 250,
        minQueryLength: 1,
        onStateChange: null,
      },
      options || {}
    );

    this.cache = new Map();
    this.cacheLimit = 30;
    this.abortController = null;
    this.lastRequestedQuery = null;
    this.state = STATES.IDLE;
    this.selectedIndex = -1;
    this.optionElements = [];

    this._onInput = debounce(this._handleInput.bind(this), this.options.debounceMs);
    this._boundOnInput = this._onInput;
    this._boundOnKeydown = this._handleKeydown.bind(this);

    this.input.addEventListener('input', this._boundOnInput);
    this.input.addEventListener('keydown', this._boundOnKeydown);

    this._setupAccessibility();
  }

  PredictiveSearchSuggestions.STATES = STATES;

  PredictiveSearchSuggestions.prototype._setupAccessibility = function _setupAccessibility() {
    if (!this.container.id) {
      this.container.id = 'predictive-search-suggestions-' + Math.random().toString(36).slice(2, 10);
    }

    this.input.setAttribute('role', 'combobox');
    this.input.setAttribute('aria-autocomplete', 'list');
    this.input.setAttribute('aria-haspopup', 'listbox');
    this.input.setAttribute('aria-expanded', 'false');
    this.input.setAttribute('aria-controls', this.container.id);
    this.input.setAttribute('autocomplete', 'off');

    this.liveRegion = document.createElement('div');
    this.liveRegion.setAttribute('aria-live', 'polite');
    this.liveRegion.setAttribute('role', 'status');
    this.liveRegion.className = 'visually-hidden predictive-search-suggestions__live-region';
    this.container.insertAdjacentElement('afterend', this.liveRegion);
  };

  PredictiveSearchSuggestions.prototype.destroy = function destroy() {
    this.input.removeEventListener('input', this._boundOnInput);
    this.input.removeEventListener('keydown', this._boundOnKeydown);
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    this.cache.clear();
    this._setState(STATES.IDLE);
    this._clearContainer();
    if (this.liveRegion && this.liveRegion.parentNode) {
      this.liveRegion.parentNode.removeChild(this.liveRegion);
    }
    this.input.removeAttribute('role');
    this.input.removeAttribute('aria-autocomplete');
    this.input.removeAttribute('aria-haspopup');
    this.input.removeAttribute('aria-expanded');
    this.input.removeAttribute('aria-controls');
    this.input.removeAttribute('aria-activedescendant');
  };

  PredictiveSearchSuggestions.prototype._handleKeydown = function _handleKeydown(event) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (!this.optionElements.length) return;
      event.preventDefault();
      var delta = event.key === 'ArrowDown' ? 1 : -1;
      var nextIndex = this.selectedIndex + delta;
      if (nextIndex < 0) nextIndex = this.optionElements.length - 1;
      if (nextIndex >= this.optionElements.length) nextIndex = 0;
      this._selectOption(nextIndex);
      return;
    }

    if (event.key === 'Enter') {
      if (this.selectedIndex > -1 && this.optionElements[this.selectedIndex]) {
        var link = this.optionElements[this.selectedIndex].querySelector('a');
        if (link) {
          event.preventDefault();
          window.location.href = link.href;
        }
      }
      // No option selected: let the native form submission proceed as-is
      // (existing "/search?q=..." behavior is preserved).
      return;
    }

    if (event.key === 'Escape') {
      this._clearSelection();
      this.input.setAttribute('aria-expanded', 'false');
    }
  };

  PredictiveSearchSuggestions.prototype._selectOption = function _selectOption(index) {
    if (this.selectedIndex > -1 && this.optionElements[this.selectedIndex]) {
      this.optionElements[this.selectedIndex].setAttribute('aria-selected', 'false');
    }
    this.selectedIndex = index;
    var option = this.optionElements[index];
    option.setAttribute('aria-selected', 'true');
    this.input.setAttribute('aria-activedescendant', option.id);
    if (typeof option.scrollIntoView === 'function') {
      option.scrollIntoView({ block: 'nearest' });
    }
  };

  PredictiveSearchSuggestions.prototype._clearSelection = function _clearSelection() {
    this.selectedIndex = -1;
    this.optionElements.forEach(function (el) {
      el.setAttribute('aria-selected', 'false');
    });
    this.input.removeAttribute('aria-activedescendant');
  };

  PredictiveSearchSuggestions.prototype._handleInput = function _handleInput() {
    var rawQuery = this.input.value;
    var trimmedQuery = rawQuery.trim();

    if (!trimmedQuery.length) {
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
      this.lastRequestedQuery = null;
      this._setState(STATES.IDLE);
      this._clearContainer();
      return;
    }

    if (trimmedQuery.length < this.options.minQueryLength) {
      return;
    }

    var normalizedQuery = normalizeQuery(trimmedQuery);

    if (normalizedQuery === this.lastRequestedQuery) {
      return;
    }

    this.lastRequestedQuery = normalizedQuery;
    this._fetchResults(trimmedQuery, normalizedQuery);
  };

  PredictiveSearchSuggestions.prototype._fetchResults = function _fetchResults(displayQuery, normalizedQuery) {
    var self = this;

    if (this.cache.has(normalizedQuery)) {
      this._renderProducts(this.cache.get(normalizedQuery), displayQuery);
      return;
    }

    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();
    var signal = this.abortController.signal;

    this._setState(STATES.LOADING);

    var routes = window.routes || {};
    var predictiveSearchUrl = routes.predictive_search_url;

    if (!predictiveSearchUrl) {
      if (window.console && typeof window.console.warn === 'function') {
        window.console.warn('[predictive-search-suggestions] window.routes.predictive_search_url is not set');
      }
      this._setState(STATES.ERROR);
      this._renderError();
      return;
    }

    // routes.predictive_search_url resolves to the bare `/search/suggest`
    // path, which is Shopify's section-rendering mode and requires a
    // section_id param. The `.json` suffix is what returns JSON instead.
    var requestUrl =
      predictiveSearchUrl +
      '.json?q=' +
      encodeURIComponent(displayQuery) +
      '&resources[type]=product' +
      '&resources[limit]=' +
      this.options.resultLimit;

    fetch(requestUrl, { signal: signal })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Predictive search request to ' + requestUrl + ' failed with status ' + response.status);
        }
        return response.json();
      })
      .then(function (data) {
        if (normalizedQuery !== self.lastRequestedQuery) {
          // A newer query has since been issued; discard this stale response.
          return;
        }

        var products = self._extractProducts(data);
        self._storeInCache(normalizedQuery, products);
        self._renderProducts(products, displayQuery);
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') {
          return;
        }
        if (window.console && typeof window.console.warn === 'function') {
          window.console.warn('[predictive-search-suggestions] request failed:', error);
        }
        if (normalizedQuery !== self.lastRequestedQuery) {
          return;
        }
        self._setState(STATES.ERROR);
        self._renderError();
      });
  };

  PredictiveSearchSuggestions.prototype._extractProducts = function _extractProducts(data) {
    try {
      var products = data && data.resources && data.resources.results && data.resources.results.products;
      if (!Array.isArray(products)) {
        return [];
      }
      return products.slice(0, this.options.resultLimit);
    } catch (error) {
      return [];
    }
  };

  PredictiveSearchSuggestions.prototype._storeInCache = function _storeInCache(normalizedQuery, products) {
    if (this.cache.size >= this.cacheLimit) {
      var oldestKey = this.cache.keys().next().value;
      this.cache.delete(oldestKey);
    }
    this.cache.set(normalizedQuery, products);
  };

  PredictiveSearchSuggestions.prototype._setState = function _setState(state) {
    this.state = state;
    this.container.setAttribute('data-predictive-search-state', state);

    var isExpanded = state === STATES.RESULTS || state === STATES.NO_RESULTS || state === STATES.ERROR;
    this.input.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
    if (!isExpanded) {
      this._clearSelection();
    }

    if (typeof this.options.onStateChange === 'function') {
      this.options.onStateChange(state);
    }
  };

  PredictiveSearchSuggestions.prototype._clearContainer = function _clearContainer() {
    this.container.innerHTML = '';
    this.optionElements = [];
    this.selectedIndex = -1;
  };

  PredictiveSearchSuggestions.prototype._renderProducts = function _renderProducts(products, displayQuery) {
    if (!products.length) {
      this._setState(STATES.NO_RESULTS);
      this._renderEmpty(displayQuery);
      return;
    }

    this._setState(STATES.RESULTS);

    var viewAllUrl = '/search?q=' + encodeURIComponent(displayQuery) + '&type=product';
    var escapedQuery = escapeHtml(displayQuery);

    var itemsMarkup = products
      .map(function (product, index) {
        return this._renderProductItem(product, index);
      }, this)
      .join('');

    this.container.innerHTML =
      '<div class="predictive-search-suggestions__heading">Products</div>' +
      '<ul class="predictive-search-suggestions__list" role="listbox" id="' +
      this.container.id +
      '-listbox">' +
      itemsMarkup +
      '</ul>' +
      '<a class="predictive-search-suggestions__view-all" href="' +
      viewAllUrl +
      '">View all results for &ldquo;' +
      escapedQuery +
      '&rdquo; &rarr;</a>';

    this.optionElements = Array.prototype.slice.call(this.container.querySelectorAll('[role="option"]'));
    this.selectedIndex = -1;

    var count = products.length;
    this.liveRegion.textContent =
      count + (count === 1 ? ' product' : ' products') + ' found for "' + displayQuery + '"';
  };

  PredictiveSearchSuggestions.prototype._renderProductItem = function _renderProductItem(product, index) {
    var title = escapeHtml(product.title || '');
    var url = escapeHtml(product.url || '#');
    var imageUrl = escapeHtml((product.image && product.image.url) || product.image || '');
    var available = product.available !== false;
    var priceMarkup = this._renderPrice(product);
    var optionId = this.container.id + '-option-' + index;

    return (
      '<li id="' +
      optionId +
      '" class="predictive-search-suggestions__item" role="option" aria-selected="false">' +
      '<a href="' +
      url +
      '" class="predictive-search-suggestions__link">' +
      (imageUrl
        ? '<img class="predictive-search-suggestions__image" src="' +
          imageUrl +
          '" alt="' +
          title +
          '" loading="lazy" width="50" height="50">'
        : '') +
      '<span class="predictive-search-suggestions__content">' +
      '<span class="predictive-search-suggestions__title">' +
      title +
      '</span>' +
      priceMarkup +
      (!available
        ? '<span class="predictive-search-suggestions__sold-out">Sold out</span>'
        : '') +
      '</span>' +
      '</a>' +
      '</li>'
    );
  };

  PredictiveSearchSuggestions.prototype._renderPrice = function _renderPrice(product) {
    // Shopify's Predictive Search API returns price/compare_at_price already
    // formatted according to the shop's money format and currency settings.
    // These values are rendered as-is; they are never recalculated here.
    if (product.price == null) {
      return '';
    }

    var currentPrice = String(product.price);
    var compareAtPrice = product.compare_at_price;
    var hasCompareAt =
      compareAtPrice != null && String(compareAtPrice).length && compareAtPrice !== product.price;

    return (
      '<span class="predictive-search-suggestions__price">' +
      (hasCompareAt
        ? '<s class="predictive-search-suggestions__price-compare">' + escapeHtml(compareAtPrice) + '</s> '
        : '') +
      '<span class="predictive-search-suggestions__price-current">' + escapeHtml(currentPrice) + '</span>' +
      '</span>'
    );
  };

  PredictiveSearchSuggestions.prototype._renderEmpty = function _renderEmpty(displayQuery) {
    var escapedQuery = escapeHtml(displayQuery);
    this.container.innerHTML =
      '<div class="predictive-search-suggestions__empty">No products found for &ldquo;' + escapedQuery + '&rdquo;</div>';
    this.optionElements = [];
    this.selectedIndex = -1;
    this.liveRegion.textContent = 'No products found for "' + displayQuery + '"';
  };

  PredictiveSearchSuggestions.prototype._renderError = function _renderError() {
    this.container.innerHTML =
      '<div class="predictive-search-suggestions__error">Search is temporarily unavailable. Please try again.</div>';
    this.optionElements = [];
    this.selectedIndex = -1;
    this.liveRegion.textContent = 'Search is temporarily unavailable.';
  };

  window.PredictiveSearchSuggestions = PredictiveSearchSuggestions;
})();
